import { useEffect, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { GoldButton, Section } from "@/components/site/Ui";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import EnquiryForm from "@/components/site/EnquiryForm";
import ConsultationBooking from "@/components/site/ConsultationBooking";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Check, Gift, Video, Youtube, Clapperboard, CalendarDays } from "lucide-react";

const PLATFORMS = ["YouTube", "TikTok", "Instagram", "Facebook", "Other"];

function ServiceBlock({ id, icon: Icon, tag, title, price, children, bonus, form, alt }) {
  return (
    <div id={id} className={`scroll-mt-28 ${alt ? "bg-[#0d0d0d] border-y border-white/5" : ""}`}>
      <Section>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <div className="grid place-items-center h-14 w-14 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
              <Icon className="text-[#D4AF37]" strokeWidth={1.5} size={26} />
            </div>
            <div className="mt-5"><Eyebrow>{tag}</Eyebrow></div>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl">{title}</h2>
            {price && <div className="mt-3 text-lg gold-text font-display">{price}</div>}
            <div className="mt-5 text-white/70 leading-relaxed space-y-4 text-[15px]">{children}</div>
            {bonus && (
              <div className="mt-6 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 flex gap-3">
                <Gift className="text-[#D4AF37] shrink-0" size={20} />
                <p className="text-sm text-white/80">{bonus}</p>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.1}>{form}</Reveal>
        </div>
      </Section>
    </div>
  );
}

export default function Services() {
  const { settings } = useSite();
  const [pricing, setPricing] = useState({});
  useEffect(() => { api.get("/services-pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const cp = settings.consultation_price ?? pricing.consultation_price;
  const paidMode = cp === undefined || cp === null ? true : Number(cp) > 0;

  return (
    <>
      <div className="relative grain overflow-hidden">
        <Section className="pb-8">
          <Reveal>
            <Eyebrow>Services</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">Simple help, made around your life.</h1>
            <p className="mt-5 text-white/70 max-w-xl text-lg">Pick what suits you. Every service is explained in plain English and designed for phone-only creators.</p>
          </Reveal>
        </Section>
      </div>

      <ServiceBlock
        id="consultation" icon={CalendarDays} tag="1:1 Consultation"
        title={"\u201CFind Your Content Path\u201D Session"} price={paidMode ? (cp ? `£${cp}` : "") : "Book online"}
        bonus="BONUS: after your session you'll receive a FREE 7-day 'Clickable Video Ideas Plan' made for your specific platform."
        form={paidMode
          ? (cp
            ? <ConsultationBooking price={cp} />
            : <div className="card-surface rounded-2xl p-8 text-center text-white/40 text-sm">Loading booking…</div>)
          : <EnquiryForm testid="consultation-form" type="consultation" submitLabel="Request My Session"
          successMsg="Your consultation request is in! We'll email you to confirm a time."
          fields={[
            { name: "name", label: "Full name", required: true },
            { name: "email", label: "Email", type: "email", required: true },
            { name: "whatsapp", label: "WhatsApp number" },
            { name: "platform", label: "Preferred platform", type: "select", options: PLATFORMS, required: true },
            { name: "goals", label: "Your goals", type: "textarea", placeholder: "What do you want to achieve?" },
            { name: "datetime", label: "Preferred date & time", full: true, placeholder: "e.g. Weekday evenings" },
          ]} />}
      >
        <p>A relaxed online session where we work out what actually fits <em>you</em> — your interests, circumstances, schedule, job and lifestyle.</p>
        <p>You'll leave knowing exactly how to start, which platforms suit you, and the content formats that will feel natural and get watched.</p>
        {settings.calendly_link && (
          <a href={settings.calendly_link} target="_blank" rel="noreferrer" className="inline-block text-[#D4AF37] underline">Or book instantly on the calendar →</a>
        )}
      </ServiceBlock>

      <ServiceBlock
        id="editing" icon={Clapperboard} tag="Done for you" title="Video Editing Services" alt price="Get a quote"
        form={<EnquiryForm testid="editing-form" type="video-editing" submitLabel="Get a Quote"
          successMsg="Quote request received! We'll reply by email shortly."
          fields={[
            { name: "name", label: "Full name", required: true },
            { name: "email", label: "Email", type: "email", required: true },
            { name: "whatsapp", label: "WhatsApp number" },
            { name: "platform", label: "Platform", type: "select", options: PLATFORMS },
            { name: "videotype", label: "Type of video", type: "select", options: ["Long-form", "Short-form (Shorts/Reels/TikTok)", "Both"] },
            { name: "count", label: "Number of videos" },
            { name: "length", label: "Video length" },
            { name: "deadline", label: "Deadline" },
            { name: "footage", label: "Link to raw footage (Drive/Dropbox)", full: true },
            { name: "notes", label: "Notes", type: "textarea" },
          ]} />}
      >
        <p>Editing for both long-form videos and short-form content (YouTube Shorts, TikTok, Instagram Reels).</p>
        <p>Send us your footage and tell us the vibe — we'll make it clean, engaging and ready to post.</p>
      </ServiceBlock>

      <ServiceBlock
        id="coaching" icon={Video} tag="Growth Coaching"
        title="Weekly 1:1 Check-ins & Feedback" price={pricing.growth_coaching_price ? `From £${pricing.growth_coaching_price} / month` : "Monthly"}
        form={<EnquiryForm testid="coaching-form" type="growth-coaching" submitLabel="Enquire About Coaching"
          successMsg="Thanks! We'll email you the coaching details."
          fields={[
            { name: "name", label: "Full name", required: true },
            { name: "email", label: "Email", type: "email", required: true },
            { name: "whatsapp", label: "WhatsApp number" },
            { name: "platform", label: "Main platform", type: "select", options: PLATFORMS },
            { name: "about", label: "Tell us where you're at", type: "textarea" },
          ]} />}
      >
        <p>Weekly one-to-one calls where we review your strategy, give honest feedback on your videos, and steadily improve your performance.</p>
        <p>Perfect if you want accountability and a real person in your corner. Available as a monthly subscription or a bundle.</p>
      </ServiceBlock>

      <ServiceBlock
        id="youtube" icon={Youtube} tag="YouTube-only" title="YouTube Launch Setup" alt
        price={pricing.youtube_setup_price ? `£${pricing.youtube_setup_price}` : "Fixed price"}
        form={<EnquiryForm testid="youtube-form" type="youtube-setup" submitLabel="Set Up My Channel"
          successMsg="Great! We'll be in touch to book your setup."
          fields={[
            { name: "name", label: "Full name", required: true },
            { name: "email", label: "Email", type: "email", required: true },
            { name: "whatsapp", label: "WhatsApp number" },
            { name: "channel", label: "Channel idea / niche", type: "textarea" },
          ]} />}
      >
        <p>We'll set up and optimise your channel together — the name, banner and profile guidance, your About section, keywords and the settings that matter.</p>
        <p>You'll also get my <strong className="text-white">pre-publishing checklist</strong>: exactly what to do before your first upload — titles, descriptions, tags, thumbnails, playlists and the basics of monetisation eligibility. No guesswork, just a clear path to your first video.</p>
      </ServiceBlock>

      <Section className="text-center">
        <Reveal>
          <h2 className="font-display text-2xl sm:text-3xl">Looking for done-for-you video ideas?</h2>
          <p className="mt-3 text-white/60">Our Content Plans give you a full month of clickable ideas for your niche.</p>
          <GoldButton as="link" to="/content-plans" className="mt-6">Explore Content Plans</GoldButton>
        </Reveal>
      </Section>
    </>
  );
}
